import { useEffect, useRef, useState } from "react";
import { VscChromeClose } from "react-icons/vsc";
import styles from "./Navbar.module.css";

import { useScrollSpy } from "../../lib/hooks/useScrollSpy";
import { useNavbarHeight } from "../../utils/contexts/NavbarContext";
import { scrollToIdWithOffset } from "../../lib/scrolling/ScrollToIdWithOffset";

interface Section {
  id: string;
  name: string;
}

const sections: Section[] = [
  { id: "welcome", name: "Welcome" },
  { id: "about", name: "About Us" },
  { id: "featuredMenu", name: "Featured Menu" },
  { id: "testimonial", name: "Testimonial" },
  { id: "openingHours", name: "Opening Hours" },
  { id: "delivery", name: "Delivery" },
  { id: "partyTray", name: "Party Tray" },
  { id: "contactUs", name: "Contact Us" },
];

const sectionIds = sections.map((section) => section.id);

function Navbar() {
  const [navExpanded, setNavExpanded] = useState(false);
  const togglerRef = useRef<HTMLButtonElement>(null);

  const { navbarHeight } = useNavbarHeight();

  const activeSection = useScrollSpy({
    sectionIds,
    rootMargin: "-40% 0px -60% 0px",
  });

  useEffect(() => {
    const navbarContent = document.getElementById("navbarContent");
    if (!navbarContent) return;

    const handleShow = () => setNavExpanded(true);
    const handleHide = () => setNavExpanded(false);

    navbarContent.addEventListener("show.bs.collapse", handleShow);
    navbarContent.addEventListener("hide.bs.collapse", handleHide);

    return () => {
      navbarContent.removeEventListener("show.bs.collapse", handleShow);
      navbarContent.removeEventListener("hide.bs.collapse", handleHide);
    };
  }, []);

  const closeMobileNavbar = () => {
    if (navExpanded) {
      togglerRef.current?.click();
    }
  };

  return (
    <header>
      <nav
        id="navbar"
        className={`navbar navbar-expand-lg fixed-top ${styles.navbar}`}
        data-bs-theme="light"
      >
        <div className="container-fluid">
          <a
            className={`navbar-brand d-flex align-items-center gap-2 ${styles.brand}`}
            href="https://aloasiankitchen.org/"
            rel="home"
            onClick={closeMobileNavbar}
          >
            <img
              className={styles.logo}
              src="/aak-logo-removebg.png"
              alt="Alo Asian Kitchen logo"
            />

            <span className={styles.brandTitle}>Alo Asian Kitchen</span>
          </a>

          <button
            ref={togglerRef}
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarContent"
            aria-controls="navbarContent"
            aria-expanded={navExpanded}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
            <VscChromeClose className="my-custom-toggler-closing" />
          </button>

          <div className="collapse navbar-collapse" id="navbarContent">
            <ul className="navbar-nav ms-auto align-items-lg-center gap-lg-2">
              {sections.map((section) => (
                <li key={section.id} className="nav-item">
                  <a
                    href={`#${section.id}`}
                    className={`nav-link ${
                      activeSection === section.id ? styles.activeLink : ""
                    }`}
                    onClick={(event) => {
                      closeMobileNavbar();
                      scrollToIdWithOffset(section.id, event, navbarHeight);
                    }}
                  >
                    {section.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;